
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Subscription, Bill } from "@/components/subscriptions/types";

export function usePersonalSubscriptions() {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      console.log('Fetching subscriptions for current user...');
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("No authenticated user");
      }

      const { data: schoolUser } = await supabase
        .from('school_users')
        .select('school_id')
        .eq('email', user.email)
        .maybeSingle();

      if (!schoolUser) {
        console.log('No school user found');
        setSubscriptions([]);
        return;
      }

      const schoolId = schoolUser.school_id;

      const { data: client } = await supabase
        .from('clients')
        .select('id')
        .eq('school_id', schoolId)
        .maybeSingle();

      if (!client) {
        console.log('No client found');
        setSubscriptions([]);
        return;
      }

      const currentDate = new Date().toISOString();

      const { data: subscriptionsData, error } = await supabase
        .from('subscriptions')
        .select(`
          id,
          status,
          start_date,
          end_date,
          auto_renewal,
          plan:plans (
            name,
            price,
            duration,
            product:products (
              name,
              description,
              product_screen,
              logo
            )
          ),
          bills (
            id, 
            amount, 
            tax, 
            discount,
            discount_type,
            status,
            due_date,
            description,
            transaction_number,
            bill_component_id
          )
        `)
        .eq('client_id', client.id)
        .or(`status.in.(active,pending,past_due),and(status.in.(cancelled,terminated),end_date.gt.${currentDate})`)
        .order('created_at', { ascending: false });

      if (error) throw error;

      console.log('Fetched subscriptions:', subscriptionsData);

      if (subscriptionsData) {
        const formattedSubscriptions = subscriptionsData.map(sub => {
          // Transform bills to match the Bill interface
          const formattedBills: Bill[] = Array.isArray(sub.bills) 
            ? sub.bills.map(bill => ({
                id: bill.id,
                amount: bill.amount || 0,
                tax: bill.tax || 0,
                discount: bill.discount || 0,
                description: bill.description,
                status: bill.status,
                due_date: bill.due_date,
                transaction_number: bill.transaction_number || '',
                discount_type: bill.discount_type,
                bill_component_id: bill.bill_component_id,
                plan_price: sub.plan?.price || 0,
                plan: {
                  name: sub.plan?.name || 'Unknown',
                  price: sub.plan?.price || 0,
                  product: {
                    name: sub.plan?.product?.name || 'Unknown'
                  }
                }
              }))
            : [];

          return {
            id: sub.id,
            status: sub.status,
            start_date: sub.start_date,
            end_date: sub.end_date,
            auto_renewal: sub.auto_renewal !== null ? sub.auto_renewal : true, // Default to true if null
            school_id: schoolId, // Add the school_id to each subscription
            plan: {
              name: sub.plan?.name || 'Unknown',
              price: sub.plan?.price || 0,
              duration: sub.plan?.duration || 0,
              product: {
                name: sub.plan?.product?.name || 'Unknown',
                description: sub.plan?.product?.description,
                product_screen: sub.plan?.product?.product_screen,
                logo: sub.plan?.product?.logo
              }
            },
            bills: formattedBills
          };
        });
        setSubscriptions(formattedSubscriptions);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch subscriptions",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    subscriptions,
    loading,
    fetchSubscriptions
  };
}
